import { computed, defineAsyncComponent, defineComponent, h } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '../stores/userStore'
import { useGlobalPermissionsStore } from '../stores/globalPermissions'
import { sessionState } from '../session/sessionExpiry.js'
import LoginPage from '../views/LoginPage.vue'
import FixturesPage from '../views/FixturesPage.vue'
import RostersPage from '../views/RostersPage.vue'
import PlayersPage from '../views/PlayersPage.vue'
import LeagueDetailsPage from '../views/LeagueDetailsPage.vue'
import TeamRosterPage from '../views/TeamRosterPage.vue'
import LeagueTeamsPage from '../views/LeagueTeamsPage.vue'
import ClientsPage from '../views/ClientsPage.vue'
import TradersPage from '../views/TradersPage.vue'
import InvoicesPage from '../views/InvoicesPage.vue'
import SettingsPage from '../views/SettingsPage.vue'
import ClientLayout from '../layouts/ClientLayout.vue'
import ClientDashboard from '../views/client/ClientDashboard.vue'
import MonitoringLayout from '../views/monitoring/MonitoringLayout.vue'
import FixtureDetailsView from '../views/FixtureDetailsView.vue'
import AnalyticsView from '../views/AnalyticsView.vue'
import MonitoringView from '../views/fixture/MonitoringView.vue'
import { activeUiTemplateId } from '../ui-template'
import { nextRouteOverrides } from '../next/routes'


const isClientRole = () => (useUserStore().$state.currentUser?.role || 'client') === 'client'

// Route component that renders the V1 fixture view for clients and the V2
// view for everyone else, so both share the /fixture/:id URL space.
// Reactive on the role, so impersonation / re-login swaps the view in place.
const fixtureViewByRole = (loadV1, loadV2) => {
  const V1 = defineAsyncComponent(loadV1)
  const V2 = defineAsyncComponent(loadV2)
  return defineComponent({
    name: 'FixtureViewByRole',
    inheritAttrs: false,
    setup(_, { attrs }) {
      const userStore = useUserStore()
      const isClient = computed(() => (userStore.$state.currentUser?.role || 'client') === 'client')
      return () => h(isClient.value ? V1 : V2, attrs)
    }
  })
}

// Helper function to determine default fixture tab based on user role
const getDefaultFixtureTab = (userRole) => {
  const roleTabMapping = {
    'client': 'balanced-lines',
    'admin': 'projections', 
    'trader': 'projections',
    'superadmin': 'projections'
  }
  
  // Return role-specific tab or default to balanced-lines for unknown roles
  return roleTabMapping[userRole] || 'balanced-lines'
}

const routes = [
  {
    path: '/login',
    name: 'login',
    component: LoginPage,
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    redirect: '/games/fixtures'
  },
  {
    path: '/games',
    redirect: '/games/fixtures'
  },
  {
    path: '/templates',
    redirect: '/games/fixtures'
  },
  {
    path: '/games/fixtures',
    name: 'fixtures',
    component: FixturesPage,
    children: [
      {
        path: '',
        name: 'fixtures-list',
        component: ()=> import('../views/FixturesListView.vue')
      },
      {
        path: 'league/:id',
        name: 'league-details',
        component: LeagueDetailsPage,
        props: true
      }
    ]
  },
  {
    path: '/games/rosters',
    name: 'rosters',
    component: RostersPage
  },
  {
    path: '/games/players',
    name: 'players',
    component: PlayersPage
  },
  {
    path: '/games/rosters/team/:teamName',
    name: 'team-roster',
    component: TeamRosterPage,
    props: true
  },
  {
    path: '/games/rosters/league/:leagueId',
    name: 'league-teams',
    component: LeagueTeamsPage,
    props: true
  },
  {
    path: '/clients',
    name: 'clients',
    component: () => import('../views/ClientsPage.vue'),
    meta: { permissions: ['clients.view', 'admin.access'] }
  },
  {
    path: '/traders',
    name: 'traders',
    component: () => import('../views/TradersPage.vue'),
    meta: { permissions: ['traders.view', 'admin.access'] }
  },
  {
    path: '/invoices',
    name: 'invoices',
    component: () => import('../views/InvoicesPage.vue'),
    meta: { permissions: ['invoices.view', 'admin.access'] }
  },
  {
    path: '/profile',
    redirect: '/settings',
    name: 'profile'
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('../views/SettingsPage.vue'),
    children: [
      {
        path: '',
        name: 'settings-general',
        component: () => import('../views/settings/GeneralSettings.vue')
      },
      {
        path: 'appearance',
        name: 'settings-appearance',
        component: () => import('../views/settings/AppearanceSettings.vue')
      },
      {
        path: 'notifications',
        name: 'settings-notifications',
        component: () => import('../views/settings/NotificationSettings.vue')
      },
      {
        path: 'security',
        name: 'settings-security',
        component: () => import('../views/settings/SecuritySettings.vue'),
        meta: { permissions: ['security.view', 'admin.access'] }
      },
      {
        path: 'integrations',
        name: 'settings-integrations',
        component: () => import('../views/settings/IntegrationSettings.vue'),
        meta: { permissions: ['integrations.view', 'admin.access'] }
      },
      {
        path: 'boxscores-management',
        name: 'settings-boxscores-management',
        component: () => import('../views/settings/BoxscoresManagementSettings.vue'),
        meta: { permissions: ['manage_boxscores', '*'] }
      }
    ]
  },
  {
    // Interface template picker. Classic shows it as a Settings tab; the
    // "next" template overrides this route with its own page.
    path: '/appearance',
    name: 'appearance',
    redirect: { path: '/settings', query: { tab: 'appearance' } }
  },
  {
    path: '/tutorials',
    name: 'tutorials',
    component: () => import('../views/TutorialsPage.vue'),
    children: [
      {
        path: '',
        redirect: '/tutorials/getting-started'
      },
      {
        path: 'getting-started',
        name: 'tutorials-getting-started',
        component: () => import('../views/tutorials/GettingStartedTutorial.vue')
      },
      {
        path: 'game-management',
        name: 'tutorials-game-management',
        component: () => import('../views/tutorials/GameManagementTutorial.vue')
      },
      {
        path: 'odds-composition',
        name: 'tutorials-odds-composition',
        component: () => import('../views/tutorials/OddsCompositionTutorial.vue')
      },
      {
        path: 'client-management',
        name: 'tutorials-client-management',
        component: () => import('../views/tutorials/ClientManagementTutorial.vue')
      },
      {
        path: 'reports-analytics',
        name: 'tutorials-reports-analytics',
        component: () => import('../views/tutorials/ReportsAnalyticsTutorial.vue')
      }
    ]
  },
  {
    path: '/outrights',
    name: 'outrights',
    component: () => import('../components/outrights/OutrightsDetails.vue'),
    meta: { permissions: ['outrights.view', 'admin.access'] },
    children: [
      {
        path: ':id',
        name: 'outright-details',
        component: () => import('../components/outrights/OutrightsDetails.vue')
      }
    ]
  },
  {
    path: '/fixture/:id',
    name: 'FixtureDetails',
    // Clients get the V1 fixture views (the "client version") under the same
    // /fixture/ URLs admins use for V2. See fixtureViewByRole() above.
    component: fixtureViewByRole(
      () => import('../views/FixtureDetailsView.vue'),
      () => import('../views/fixture-v2/FixtureV2DetailsView.vue')
    ),
    redirect: to => {
      const userStore = useUserStore()
      const userRole = userStore.$state.currentUser?.role || 'client'
      return `/fixture/${to.params.id}/${getDefaultFixtureTab(userRole)}`
    },
    children: [
      {
        path: 'projections',
        name: 'FixtureProjections',
        component: fixtureViewByRole(
          () => import('../views/fixture/ProjectionsTab.vue'),
          () => import('../views/fixture-v2/ProjectionTab.vue')
        ),
        meta: { permissions: ['fixtures.projections'] }
      },
      {
        path: 'projection',
        redirect: to => `/fixture/${to.params.id}/projections`
      },
      {
        path: 'balanced-lines',
        name: 'FixtureBalancedLines',
        component: fixtureViewByRole(
          () => import('../views/fixture/BalancedLinesTab.vue'),
          () => import('../views/fixture-v2/BalancedLinesTab.vue')
        )
      },
      {
        path: 'milestones',
        name: 'FixtureMilestones',
        component: fixtureViewByRole(
          () => import('../views/fixture/MilestonesTab.vue'),
          () => import('../views/fixture-v2/MilestonesTab.vue')
        )
      },
      {
        // In-play V2 points ladder over SSE (milestones-live hub). V2 only, admin role only for now.
        path: 'milestones-live',
        name: 'FixtureMilestonesLive',
        component: () => import('../views/fixture-v2/MilestonesLiveTab.vue'),
        meta: { role: 'admin' }
      },
      {
        path: 'settlement',
        name: 'FixtureSettlement',
        component: fixtureViewByRole(
          () => import('../views/fixture/SettlementTab.vue'),
          () => import('../views/fixture-v2/SettlementTab.vue')
        )
      },
      {
        path: 'specials',
        name: 'FixtureSpecials',
        component: fixtureViewByRole(
          () => import('../views/fixture/SpecialsTab.vue'),
          () => import('../views/fixture-v2/SpecialsTab.vue')
        )
      },
      {
        path: 'live',
        name: 'FixtureLive',
        component: fixtureViewByRole(
          () => import('../views/fixture/InplayTab.vue'),
          () => import('../views/fixture-v2/LiveTab.vue')
        ),
        meta: { permissions: ['fixtures.inplay'] }
      },
      {
        // In-play V2 console (backend engine). Reached by URL only; V1 'live' is unchanged.
        path: 'live-v2',
        name: 'FixtureLiveV2',
        component: () => import('../views/fixture-v2/live-v2/LiveV2Tab.vue'),
        meta: { permissions: ['fixtures.inplay'] }
      },
      {
        path: 'inplay',
        redirect: to => `/fixture/${to.params.id}/live`
      },
      {
        path: 'monitoring',
        redirect: to => `/fixture-v1/${to.params.id}/monitoring`
      }
    ]
  },
  {
    path: '/fixture-v1/:id',
    name: 'FixtureV1Details',
    component: FixtureDetailsView,
    redirect: to => `/fixture-v1/${to.params.id}/${getDefaultFixtureTab(useUserStore().$state.currentUser?.role || 'client')}`,
    // /fixture-v1/ is the admin-only escape hatch to the V1 views; clients
    // already get V1 under /fixture/, so never show them the -v1 URL.
    beforeEnter: to => (isClientRole() ? to.fullPath.replace(/^\/fixture-v1\//, '/fixture/') : true),
    children: [
      {
        path: 'projections',
        name: 'FixtureV1Projections',
        component: () => import('../views/fixture/ProjectionsTab.vue'),
        meta: { permissions: ['fixtures.projections'] }
      },
      {
        path: 'balanced-lines',
        name: 'FixtureV1BalancedLines',
        component: () => import('../views/fixture/BalancedLinesTab.vue')
      },
      {
        path: 'milestones',
        name: 'FixtureV1Milestones',
        component: () => import('../views/fixture/MilestonesTab.vue')
      },
      {
        path: 'settlement',
        name: 'FixtureV1Settlement',
        component: () => import('../views/fixture/SettlementTab.vue')
      },
      {
        path: 'specials',
        name: 'FixtureV1Specials',
        component: () => import('../views/fixture/SpecialsTab.vue')
      },
      {
        path: 'inplay',
        name: 'FixtureV1Inplay',
        component: () => import('../views/fixture/InplayTab.vue'),
        meta: { permissions: ['fixtures.inplay'] }
      },
      {
        path: 'monitoring',
        name: 'fixture-monitoring',
        component: MonitoringView,
        meta: { permissions: ['monitoring.view', 'admin.access'] }
      }
    ]
  },
  {
    path: '/fixture-v2/:id/:tab?',
    redirect: to => {
      const tab = to.params.tab
      if (tab === 'projection' || !tab) {
        return `/fixture/${to.params.id}/projections`
      }
      return `/fixture/${to.params.id}/${tab}`
    }
  },
  {
    path: '/server',
    component: () => import('../views/ServerManagerView.vue'),
    meta: { permissions: ['server.access', 'admin.access'] },
    children: [
      {
        path: 'mysql',
        component: () => import('../views/server/MySQLManagerView.vue'),
        meta: { permissions: ['database.view', 'server.access'] }
      },
      {
        path: 'backups',
        component: () => import('../views/server/BackupsView.vue'),
        meta: { permissions: ['backups.view', 'server.access'] }
      },
      {
        path: 'restore',
        component: () => import('../views/server/RestoreView.vue'),
        meta: { permissions: ['backups.restore', 'server.access'] }
      },
      {
        path: 'kubernetes',
        component: () => import('../views/server/KubernetesView.vue'),
        meta: { permissions: ['kubernetes.view', 'server.access'] }
      },
      {
        path: 'docker',
        component: () => import('../views/server/DockerView.vue'),
        meta: { permissions: ['docker.view', 'server.access'] }
      },
      {
        path: 'metrics',
        component: () => import('../views/server/MetricsView.vue'),
        meta: { permissions: ['metrics.view', 'server.access'] }
      },
      {
        path: 'logs',
        component: () => import('../views/server/LogsView.vue'),
        meta: { permissions: ['logs.view', 'server.access'] }
      }
    ]
  },
  {
    path: '/client',
    component: ClientLayout,
    meta: { requiresAuth: true, role: 'client' },
    children: [
      {
        path: '',
        name: 'client-dashboard',
        component: ClientDashboard
      },
      {
        path: 'games',
        name: 'client-games',
        component: () => import('../views/client/ClientGames.vue')
      },
      {
        path: 'api',
        name: 'client-api',
        component: () => import('../views/client/ClientApi.vue')
      },
      {
        path: 'billing',
        name: 'client-billing',
        component: () => import('../views/client/ClientBilling.vue')
      }
    ]
  },
  {
    path: '/monitoring',
    component: MonitoringLayout,
    meta: { permissions: ['monitoring.view', 'admin.access'] },
    children: [
      {
        path: '',
        redirect: '/monitoring/arbitrage'
      },
      {
        path: 'arbitrage',
        name: 'monitoring-arbitrage',
        component: () => import('../views/monitoring/ArbitrageView.vue')
      },
      {
        path: 'alerts',
        name: 'monitoring-alerts', 
        component: () => import('../views/monitoring/AlertsView.vue')
      },
      {
        path: 'history',
        name: 'monitoring-history',
        component: () => import('../views/monitoring/HistoryView.vue')
      }
    ]
  },
  {
    path: '/analytics',
    name: 'analytics',
    component: AnalyticsView,
    meta: { permissions: ['analytics.view', 'admin.access'] }
  },
  {
    path: '/automatic-projections-settings/:leagueId/:teamId/:fixtureId',
    name: 'automatic-projections-settings',
    component: () => import('../views/AutomaticProjectionsSettingsView.vue'),
    props: true,
    meta: { permissions: ['projections.edit', 'admin.access'] }
  },
  {
    path: '/watchlist-popup',
    name: 'watchlist-popup',
    component: () => import('../views/WatchListPopupView.vue'),
    meta: { requiresAuth: true }
  },
  // {
  //   path: '/boxscores',
  //   name: 'boxscores',
  //   component: () => import('../views/BoxscoresPage.vue'),
  //   meta: { permissions: ['manage_boxscores', '*'] }
  // }
  {
    path: '/speed-testing',
    name: 'speed-testing',
    component: () => import('../components/speed-testing/SpeedTestingView.vue'),
    meta: { requiresAuth: false }
  },
  {
    // Reachable from the global context menu ("Messageboard"); not in the sidebar nav yet.
    path: '/messageboard',
    name: 'messageboard',
    component: () => import('../views/MessageboardView.vue')
  }
]

// Router-level template flip: the active interface template replaces route
// components by route name; routes it doesn't override keep the classic page.
const routeOverrides = activeUiTemplateId === 'next' ? nextRouteOverrides : {}
const activeRoutes = routes.map(record => routeOverrides[record.name]
  ? { path: record.path, name: record.name, meta: record.meta, ...routeOverrides[record.name] }
  : record)

const router = createRouter({
  history: createWebHistory(),
  routes: activeRoutes
})

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()
  
  // Check authentication first
  if (to.matched.some(record => record.meta.requiresAuth !== false)) {
    const isAuthenticated = await userStore.checkAuth()
    
    if (!isAuthenticated) {
      // Session expired mid-use: stay on the current page, the re-login popup is open
      if (sessionState.expired) {
        next(false)
        return
      }
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
      return
    }

    // Check role-based access
    if (to.meta.role && userStore.currentUser?.role !== to.meta.role) {
      next(userStore.currentUser?.role === 'client' ? '/client' : '/')
      return
    }

    // Check permission-based access
    const permissionsStore = useGlobalPermissionsStore()
    await permissionsStore.fetchCurrentUserPermissions()
    
    // Check if any route in the matched routes has permission requirements
    const requiresPermissions = to.matched.some(record => record.meta.permissions)
    
    if (requiresPermissions) {
      // Get all permission requirements from matched routes
      const allRequiredPermissions = []
      to.matched.forEach(record => {
        if (record.meta.permissions) {
          allRequiredPermissions.push(...record.meta.permissions)
        }
      })
      
      // Check if user has any of the required permissions (OR logic)
      const hasRequiredPermission = permissionsStore.currentUserHasAnyPermission(allRequiredPermissions)
      
      if (!hasRequiredPermission) {
        // Redirect based on user role if no permission
        const redirectPath = userStore.currentUser?.role === 'client' ? '/client' : '/games/fixtures'
        next({ path: redirectPath })
        return
      }
    }
  }
  
  // Handle login redirect for authenticated users
  if (to.name === 'login' && userStore.isAuthenticated) {
    next({ path: userStore.currentUser?.role === 'client' ? '/client' : '/' })
    return
  }
  
  next()
})

export default router 